import React, { useEffect, useState } from "react";
import { Button, Badge } from "react-bootstrap";
import api from "../../api";
import { LoadingState } from "../../components/ui/LoadingState";
import { ErrorState } from "../../components/ui/ErrorState";
import EmptyState from "../../components/common/EmptyState";
import { FaLock, FaSignal } from "react-icons/fa";
import "./ProjectWorkspace.css";
import "../uptime/uptimeMonitoring.css";

const STATUS_TONE = { UP: "success", DOWN: "danger", PENDING: "warning", MAINTENANCE: "secondary", UNKNOWN: "secondary" };

// Project Owner/Member "Uptime" tab -- GET /projects/:id/uptime-monitors,
// scoped ENTIRELY server-side to monitors mapped to THIS project (see
// controllers/uptimeMonitorController.js#getMonitorsForProject -- the
// allowed project is derived from the caller's own owner/member/module-
// member relationship, never trusted from this URL param). This tab never
// shows another project's monitors or General/Unmapped monitors -- it
// simply renders whatever the backend returns for this exact project id,
// same "backend is the only authority" posture as every other tab in this
// shared ProjectWorkspacePage.
export default function ProjectWorkspaceUptimeTab({ projectId }) {
  const [monitors, setMonitors] = useState([]);
  const [liveStatusAvailable, setLiveStatusAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [forbidden, setForbidden] = useState(false);

  const load = () => {
    setLoading(true);
    setError("");
    setForbidden(false);
    api
      .get(`/projects/${projectId}/uptime-monitors`)
      .then((res) => {
        setMonitors(Array.isArray(res.data?.data) ? res.data.data : []);
        setLiveStatusAvailable(res.data?.live_status_available !== false);
      })
      .catch((err) => {
        if (err.response?.status === 403) setForbidden(true);
        setError(err.response?.data?.message || "Failed to load uptime monitors");
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <LoadingState message="Loading uptime monitors…" />;
  if (forbidden) {
    return (
      <EmptyState
        icon={FaLock}
        title="Uptime monitoring is not enabled for your role"
        message="Your account can view this project, but not its uptime monitors. Ask an Admin to grant the Uptime Monitoring “view project” permission in Access Management if you need it."
      />
    );
  }
  if (error) return <ErrorState message={error} action={<Button onClick={load}>Retry</Button>} />;
  if (monitors.length === 0) {
    return (
      <EmptyState
        icon={FaSignal}
        title="No monitors mapped to this project"
        message="An Admin/Manager can map an existing Uptime Kuma monitor to this project from Uptime Monitoring → Projects."
      />
    );
  }

  const upCount = monitors.filter((m) => m.current_status === "UP").length;
  const downCount = monitors.filter((m) => m.current_status === "DOWN").length;

  return (
    <div className="pw-uptime-tab">
      <div className="um-project-summary">
        <span>{monitors.length} monitor{monitors.length === 1 ? "" : "s"}</span>
        <span className="um-project-summary-up">{upCount} up</span>
        <span className="um-project-summary-down">{downCount} down</span>
        {!liveStatusAvailable && (
          <Badge bg="secondary" className="um-live-badge">Live status unavailable — showing last synced data</Badge>
        )}
      </div>

      <div className="um-monitor-cards">
        {monitors.map((m) => (
          <div className="um-monitor-card" key={m.id}>
            <div className="um-monitor-card-head">
              <strong>{m.name}</strong>
              <Badge bg={STATUS_TONE[m.current_status] || "secondary"}>{m.current_status}</Badge>
            </div>
            {m.url && <div className="um-monitor-url" title={m.url}>{m.url}</div>}
            <div className="um-monitor-meta">
              <span>{m.last_response_time_ms != null ? `${m.last_response_time_ms} ms` : "—"}</span>
              <span>{m.last_checked_at ? new Date(m.last_checked_at).toLocaleString() : "—"}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
